import React, {
  useEffect,
  useState,
  useRef,
  createRef,
  useMemo,
  useCallback
} from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { push } from 'connected-react-router';

import isPlainObject from 'lodash/isPlainObject';

import {
  Column,
  Grouping,
  GroupPanel,
  Pager,
  Paging,
  SearchPanel,
  Sorting,
  Scrolling,
  Selection,
  FilterPanel,
  FilterBuilder,
  FilterBuilderPopup,
  HeaderFilter,
  ColumnFixing,
  StateStoring
} from 'devextreme-react/data-grid';
import CustomStore from "devextreme/data/custom_store";
import DataSource from 'devextreme/data/data_source';
import { Template } from 'devextreme-react/core/template';
import ContextMenu from 'devextreme-react/context-menu';
import Query from 'devextreme/data/query';

import { Link } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import CircularProgress from "@material-ui/core/CircularProgress";

import { format as dateFormat } from 'date-fns';
import { formatValue } from 'utilities/format';

import {
  setPostAcqWorkflowGridLayoutIsDiverted,
  appNotify,
  setPostAcqWorkflowGridNeedsToRefresh,
  setPostAcqWorkflowGridSelectedLayoutId
} from 'store/actions';
import { postAcqWorkflowGridOldestLayoutIdSelector } from 'store/selectors';
import { dataGridExtension } from 'components/devExtreme';
import CustomColumnChooser from 'components/customColumnChooser';
import DataGrid from 'components/devExtreme/DataGrid';

import { Constants } from 'utilities/constants';
import { GetContextMenu } from 'api/shared/contextMenu';

// import './ChannelWorkflowGrid.css';
// import './ChannelWorkflowGridOverride.css';

import { postWithToken, postWithTokenV2 } from "api/network";

import { getChipStatus as getTaskChipStatus } from 'components/underwrite/postAcquisition/TaskStatusChip';
import { getChipStatus as getUnderwriteChipStatus } from 'components/underwrite/postAcquisition/UnderwriteStatusChip';
import { cleanState } from 'components/devExtreme/DataGrid';
import ArrayStore from 'devextreme/data/array_store';
import { columnSchema } from 'components/underwrite/postAcquisition/postAcqWorkflow/gridConfig';

// let t0, t1;

const contextMenuRef = createRef();

// solution from devextreme support team https://supportcenter.devexpress.com/ticket/details/t998890/datagrid-column-headerfilter-customization
function calculateFilterExpression(
  filterValue,
  selectedFilterOperation,
  target
) {
  if (target === 'headerFilter') {
    return [this.dataField, selectedFilterOperation, filterValue];
  }
  return this.defaultCalculateFilterExpression.apply(this, arguments);
}

const isDateOrDateTime = value => {
  const regex = /^(\d{4}-[0-1]\d-[0-3]\d)T([0-2]\d:[0-5]\d:[0-5]\d)(\.\d+|$)$/gm;
  return regex.test(value);
}

const dateSerializationFormat = "yyyy-MM-dd'T'HH:mm:ss.SSS";

const contextMenuMaxRowCount = 20000;

const ChannelWorkflowGrid = 
  React.forwardRef(({
    appNotify,
    appSettings,
    assignTasks,
    postAcqWorkflowGridLayoutIsDiverted,
    postAcqWorkflowGridLayouts,
    postAcqWorkflowGridState,
    postAcqWorkflowGridDataUpdate,
    postAcqWorkflowGridOldestLayoutId,
    classes,
    user,
    handleDeleteTasks,
    handleCloseTasks,
    handleOpenTasks,
    handleOpenAssign,
    handleViewOnExplore,
    handleLoadTasks,
    handleUnassign,
    handleEditTask,
    push,
    setGridLoading,
    setPostAcqWorkflowGridLayoutIsDiverted,
    setPostAcqWorkflowGridSelectedLayoutId,
    setPostAcqWorkflowGridNeedsToRefresh,
    updateGrid
  }, gridRef) => {
    const [dataSource, setDataSource] = useState(null);
    // visible count value which includes dataSource and signalR changes
    const [visibleRowsCount, setVisibleRowsCount] = useState(0);
    const [gridTotalCount, setGridTotalCount] = useState(0);
    // visible count value based on dataSource
    const [dataSourceVisibleCount, setDataSourceVisibleCount] = useState(null);
    const listenForDivertedChanges = useRef(true);
    const [isColumnChooserVisible, setIsColumnChooserVisible] = useState(false);
    const selectAllIsLoading = useRef(false);

    // close column chooser filter after change location
    const location = useLocation();
    useEffect(() => {
      setIsColumnChooserVisible(false);
    }, [location]);

    /* ChannelWorkflowGrid updates its data in two ways:
      - Full rebuild of the datasource. Slow, used when replacing entire data collection in redux (ie refresh button or changing API-level
        data filter). Triggered by clientTxnOpps redux state.
      - Internal datasource methods. Fast, used when updating single rows from incoming signalR updates. Triggered by postAcqWorkflowGridDataUpdate redux
        state, which provides the key and updated data. clientTxnOpps state is also updated by the saga to keep it in sync with grid, so the
        useEffect hook will tell the grid to ignore this and prevent unnecessary DataSource rebuild.
    */

    // Update data via grid internal methods when receiving signalR update
    useEffect(() => {
      if (postAcqWorkflowGridDataUpdate && gridRef) {
        // console.log(postAcqWorkflowGridDataUpdate)
        let newGridTotalCount = gridTotalCount;
        let newGridVisibleCount = visibleRowsCount;
        Object.keys(postAcqWorkflowGridDataUpdate.data).forEach(id => {
          gridRef.current.instance.getDataSource()?.store() &&
            gridRef.current.instance.getDataSource().store().push([{ type: postAcqWorkflowGridDataUpdate.data[id].type, data: postAcqWorkflowGridDataUpdate.data[id].data, key: parseInt(id) }]);
          if (postAcqWorkflowGridDataUpdate.data[id].type === 'remove') {
            newGridTotalCount--;
            newGridVisibleCount--;
          }
        });
        setGridTotalCount(newGridTotalCount);
        setVisibleRowsCount(newGridVisibleCount);

        let serverCount = postAcqWorkflowGridDataUpdate.totalCount;
        if (serverCount != null && newGridTotalCount < serverCount) {
          setPostAcqWorkflowGridNeedsToRefresh(true);
        }
      }
    }, [postAcqWorkflowGridDataUpdate]);

    const stateHasColumns = gridRef.current?.instance.state().columns != null;
    useEffect(() => { // Effect for setting grid sort/filter/groupBy/etc via redux
      if (!gridRef?.current || !postAcqWorkflowGridState || !stateHasColumns) return;
      let newState;
      if (!postAcqWorkflowGridState) {
        newState = null
      } else if (postAcqWorkflowGridState.isUpdate) {
        const prevState = gridRef.current.instance.state();
        const { columnMods, ...rest } = postAcqWorkflowGridState.state;
        newState = {
          ...prevState,
          ...rest
        };
        if (columnMods?.length) {
          columnMods.forEach(columnMod => {
            const { field, type, value } = columnMod;
            const foundColumn = newState.columns.find(col => col.dataField === field);
            if (!foundColumn) return;
            foundColumn[type] = value;
          });
        }
        console.log(newState)
      } else {
        newState = postAcqWorkflowGridState.state;
      }
      try {
        if (!postAcqWorkflowGridState.isUpdate) {
          listenForDivertedChanges.current = false;
        }
        // const fixedColumns = appSettings.FieldSettings.filter( // Pass in fixedColumns to override user layout with values from FieldSettings for frozen columns 
        //   field => field.channelWorkflowGrid && field.channelWorkflowGrid.fixed === true
        // ).map(field => field.field);
        const fixedColumns = null; // Null here will use column schema config to determine fixed columns
        gridRef.current.instance.beginUpdate();
        gridRef.current.instance.option('grouping', { autoExpandAll: !(postAcqWorkflowGridState?.state?.collapseGroups) }); // Expand groups by default unless otherwise specified in redux grid state.
        gridRef.current.instance.state(cleanState(newState, fixedColumns)); // When switching to a layout with a filter on a deleted column, applying this state will throw grid error E1047, to be caught below
        gridRef.current.instance.endUpdate();
        createDataSource();
      } catch(e) {
        gridRef.current.instance.endUpdate();
        console.log(e)
        appNotify(`Failed to load: ${e.message}`, 'error');
        gridRef.current.instance.option('dataSource', null); // Clear dataSource on error to prevent grid crashing and show user that layout is broken
        setPostAcqWorkflowGridSelectedLayoutId(postAcqWorkflowGridOldestLayoutId, 'PostAcquisition');
      }
    }, [postAcqWorkflowGridState, stateHasColumns]);

    const isFirst = useRef(true);
    useEffect(() => {
      if (!postAcqWorkflowGridLayouts || !isFirst.current) return;
      if (!postAcqWorkflowGridLayouts.length) { // If no layouts found in db, fetch grid data using initial no-layout state. (Otherwise if layouts are found, useEffect in LayoutSelect.js will set a layout id which triggers channelWorkflowGridLayoutSaga to set postAcqWorkflowGridState and trigger above useEffect)
        isFirst.current = false; // Only run on first fetch of layouts, otherwise refreshes will also trigger recreating dataSource
        createDataSource();
      }
    }, [postAcqWorkflowGridLayouts]);
    useEffect(() => {
      createDataSource();
    }, [])

    useEffect(() => {
      const condition = dataSourceVisibleCount != null
        && dataSourceVisibleCount !== -1;

      if (condition) {
        setTimeout(() => setVisibleRowsCount(dataSourceVisibleCount));
      }
    }, [dataSourceVisibleCount]);

    useEffect(() => {
      if (!dataSource) return;
      createDataSource();
    }, [updateGrid]);

    function getGridRefState() {
      if (gridRef?.current?.instance) {
        return gridRef.current.instance.state();
      }
      return null;
    }

    const getColumns = useCallback(() => {
      // return appSettings.FieldSettings.filter(
      //   field => field.channelWorkflowGrid && field.field !== 'placeholder'
      // );
      return columnSchema;
    }, [appSettings.FieldSettings]);

    function getCurrentVisibleColumns() {
      const state = getGridRefState();
      return state?.columns && state
        .columns.filter(column => column.visible && column.dataField)
        .map(column => column.dataField);
    }

    function createDataSource() {
      listenForDivertedChanges.current = false;
      // if (!postAcqWorkflowGridState) return;
      setGridLoading(true);

      const store = new CustomStore({
        key: 'id',
        load: function (loadOptions) {
          if (isPlainObject(loadOptions.group)) {
            loadOptions.group = [loadOptions.group];
          }

          // we are appending sorting by id to ensure that the order is always the same and in descending order
          if (!loadOptions.sort) {
            loadOptions.sort = [{ selector: 'id', desc: true }];
          } else if (
            loadOptions.sort.filter((s) => s.selector === 'id').length === 0
          ) {
            loadOptions.sort = [
              ...loadOptions.sort,
              { selector: 'id', desc: true },
            ];
          }

          let params = {};

          if (loadOptions.select && loadOptions.select.length) {
            params.select = loadOptions.select.filter(
              (value) => value !== undefined
            );
          } else {
            params.select = getCurrentVisibleColumns();
          }

          if (params.select && !params.select.includes('postAcquisitionLoadCompletedOn')) {
            params.select.push('postAcquisitionLoadCompletedOn');
          }
          if (params.select && !params.select.includes('assignedUser')) {
            params.select.push('assignedUser');
          }

          // we need to add "id" in case when we deselect "ID" column from columnChooser. CustomStore uses "id" for key
          if (params.select && !params.select.includes('id')) {
            params.select.push('id');
          }

          if (params.select && !params.select.includes('asgPropID')) {
            params.select.push('asgPropID');
          }

          dataGridExtension.customStoreRequestParams.forEach((i) => {
            if (
              i in loadOptions &&
              dataGridExtension.isNotEmpty(loadOptions[i])
            ) {
              params[i] = loadOptions[i];
            }
          });
          let isClientTake = false;
          if (loadOptions.group?.length === 1) { // Captures when header filter uses 'group' to make requests
            const field = loadOptions.group[0].selector;
            const type = getColumns().find(col => col.field === field)?.type;
            const headerFilter = getColumns().find(col => col.dataField === field)?.headerFilter;
            if (type === 'number' || type === 'date' || type === 'datetime') {
              params = dataGridExtension.prepareSortingForRequest(params, field, type, headerFilter); // Get default sort order here, also filters out when user actually groups by one field
              delete params.take; // Remove 'take' to get full set of values so we can check for Blanks
              isClientTake = true; // Set flag to indicate results should be filtered down before passing to grid
            }
          }

          const url = '/api/postAcquisition/tasks/taskgrid';

          return postWithToken(url, { loadOptions: params })
            .then((response) => response.json())
            .then((data) => {
              setGridTotalCount(data.absoluteTotalCount);
              setPostAcqWorkflowGridNeedsToRefresh(false);

              // t0 = performance.now();

              let parsed = data.data;
              if (parsed[0]?.hasOwnProperty('key') && isDateOrDateTime(parsed.find(el => el.key != null)?.key)) { // If 'key' property is present (true for header filter data requests), find first non-null key and check if it is a date
                parsed = parsed.map(el => { // Map all non-null keys that are dates into Date objects
                  const hasKeyDate = el.key != null && isDateOrDateTime(el.key); 
                  if (hasKeyDate) {
                    return {
                      ...el,
                      key: new Date(el.key)
                    }
                  }
                  return el
                })
              }

              if (isClientTake) {
                let blanksElement = null;
                const blanksIndex = parsed.findIndex( // Search for a 'key' value that will produce Blanks option in header filter
                  item => dataGridExtension.checkIsBlanks(item.key)
                );
                if (blanksIndex > 0 && loadOptions.skip === 0) { // If initial request for header filter (skip === 0), splice out the Blanks item
                  blanksElement = parsed.splice(blanksIndex, 1)?.[0];
                }

                parsed = parsed.slice(0, loadOptions.take) // Filter down results to original 'take' size
                if (blanksElement) {
                  parsed.unshift(blanksElement); // Insert Blanks item at beginning
                }
              }

              const dataObj = {
                data: parsed,
                totalCount: data.totalCount
              };
              return dataObj;
            })
            .catch((error) => {
              console.log('catched error');
              appNotify(error.message, 'error');
              showRestoreOption();

              return { data: [], totalCount: 0 };
            })
            .finally(() => {
              setGridLoading(false);
              return { data: [], totalCount: 0 };
            });
        },
      });
      try {
        const newDataSource = new DataSource({
          store,
          pushAggregationTimeout: 100,
          onLoadError: (e) => console.log(e),
        });
        setDataSource(newDataSource); // Set dataSource into state so that it still triggers component updates and checks.
        gridRef.current.instance.option('dataSource', newDataSource); // Set dataSource via grid method to be able to catch when a layout has a filter on a deleted column and throws error E1047
      } catch (e) {
        console.log(e);
        appNotify(`Failed to load: ${e.message}`, 'error');
        gridRef.current.instance.option('dataSource', null); // Clear dataSource on error to prevent grid crashing and show user that layout is broken
        setPostAcqWorkflowGridSelectedLayoutId(
          postAcqWorkflowGridOldestLayoutId,
          'PostAcquisition'
        );
      }
    }

    function onContentReady(grid) {
      listenForDivertedChanges.current = true;
      // t1 = performance.now();
      // console.log('Grid render time', t1 - t0);
      // t0 = null;
      // t1 = null;
      // Set custom page count message
      const totalCount = grid.component.totalCount();
      const start = totalCount ? grid.component.pageIndex() * grid.component.pageSize() + 1 : 0;
      const end = totalCount ? start + grid.component.getVisibleRows().length - 1 : 0;
      const message = `Viewing ${start} - ${end} of ${totalCount.toLocaleString('en-US')} items`;
      const pageCount = grid.component.pageCount();
      const pageIndex = grid.component.pageIndex();
      const defaultMessage = `Page ${pageIndex + 1} of ${pageCount} (${totalCount.toLocaleString('en-US')} items)`;
      let groupingIsNotApplied = true;

      let columns = grid.component.getVisibleColumns();
      for (let i = 0; i < columns.length; i++) {
        if (columns[i].groupIndex !== undefined) {
          grid.component.option('pager', { infoText: defaultMessage });
          groupingIsNotApplied = false;
          break;
        }
      }
      if (groupingIsNotApplied) {
        grid.component.option('pager', { infoText: message });
      }

      let rows = grid.component.getVisibleRows();

      if (rows.length > 0) {
        rows.forEach(row => {
          let rowEl = grid.component.getRowElement(row.rowIndex);
          if (rowEl) {
            if (row.data.isProcessing) {
              if (rowEl[0]) {
                rowEl[0].classList.add('loadingRowColor');
              }
              if (rowEl[1]) {
                rowEl[1].classList.add('loadingRowColor');
                rowEl[1].classList.add('loadingRow');
              }
            } else {//Need this because of repaintChangesOnly=true
              if (rowEl[0] && row.data.isProcessing === false) {
                rowEl[0].classList.remove('loadingRowColor');
              }
              if (rowEl[1] && row.data.isProcessing === false) {
                rowEl[1].classList.remove('loadingRowColor');
                rowEl[1].classList.remove('loadingRow');
              }
            }
          }
        });
      }

      setDataSourceVisibleCount(grid.component.totalCount());
    }

    function onRestore(){
      gridRef.current.instance.getDataSource().reload();
      return true;
    }

    function getRestoreButton() {
      return (
      <Link
        component="button"
        color="inherit"
        onClick={ onRestore }
        underline="always"
        style= {{ marginLeft: -12 }}
      >
        try again.
      </Link>);
    }

    function showRestoreOption() {
      appNotify(
        'Last grid data request failed. Change filter/sorting or ',
        'warning',
        { persist: true },
        { additionalAction: getRestoreButton });
    }

    //#region Toolbar region
    const onToolbarPreparing = e => {
      e.toolbarOptions.items.unshift(
        {
          location: 'before',
          template: 'excludedCount'
        },
      );
      e.toolbarOptions.items.unshift(
        {
          location: 'before',
          template: 'totalCount'
        }
      );
      // this adds a column chooser icon so we can customize its behaviour
      e.toolbarOptions.items.unshift({
        widget: "dxButton",
        location: "after",
        options: {
          icon: "columnchooser",
          elementAttr: {
            id: "workflowGridColumnChooserBtn"
          },
          onClick: () => setIsColumnChooserVisible(true)
        }
      });
    }

    const toolbarItemRender = (e) => {
      switch (e.template) {
        case 'totalCount':
          return totalCountTemplate();
        case 'excludedCount':
          return excludedCountTemplate();
        default:
          return;
      }
    }

    const totalCountTemplate = (e) => {
      const { informer, countLabel, totalCountText } = classes;

      return (
        <div className={informer}>
          <span className={countLabel}>{formatValue(gridTotalCount, { type: 'fixed', useSeparator: true })}</span>
          <span className={totalCountText}>Total Count</span>
        </div>
      );
    }

    const excludedCountTemplate = (e) => {
      const totalCount = visibleRowsCount;
      const filter = gridRef?.current?.instance?.getCombinedFilter();
      const { exclusionInformer, countLabel, totalCountText } = classes;
      return filter
        ? <div className={exclusionInformer}>
          <span className={countLabel}>{totalCount > -1
            ? formatValue(totalCount, { type: 'fixed', useSeparator: true })
            : 0}
          </span>
          <span className={totalCountText}>Visible</span>
        </div>
        : null
    }

    //#region CONTEXT MENU region
    function handleContextMenu(e) {
      if (e.target === 'content') {
        // e.items can be undefined
        if (!e.items) e.items = [];
        if (e.row) {
          const { rowType } = e.row;
          if (rowType === 'data') {
            const ref = gridRef.current.instance;
            if (!ref.isRowSelected(e.row.key)) {
              ref.selectRows([e.row.key]);
            }
            e.items = []; // hide built-in context menu items since it doesn't support getting asynchronous items from server
            showCustomContextMenu(e); // fetch menu items from server and show custom context menu

          } else if (rowType === 'group') { }
        }
      }
    }

    function sortByThenBy(array, sortInfo) {
      var query = Query(array).sortBy(sortInfo[0].selector, sortInfo[0].desc);
      for(var i = 1; i < sortInfo.length; i++){
        query = query.thenBy(sortInfo[i].selector, sortInfo[i].desc);
      }
      query = query.thenBy("id", sortInfo[sortInfo.length - 1].desc);

      return query.toArray();
    }

    async function showCustomContextMenu(e) {
      /* We fetch asynchronous context menu items from server*/
      const sortInfo = gridRef.current.instance.getDataSource().sort();
      let rows = gridRef.current.instance.getSelectedRowsData();
      rows = Query(rows).sortBy("id", true).toArray();
      if(sortInfo?.length > 0) {
        rows = sortByThenBy(rows, sortInfo);
      }
      const selectedRows = rows;
      if (!selectedRows.length) return;
      const cellValue = formatColumns(e.row.data, e.column, false);

      // show a empty context menu with loading spinner before we call backend server
      contextMenuRef.current.instance.option({ items: [{ text: "loading" }], target: e.targetElement });
      contextMenuRef.current.instance.show();

      try {
        // Call server to get context menu items
        const data = await postWithTokenV2(`/api/postAcquisition/tasks/taskgrid/contextmenu`, selectedRows.map(row => row.id));
        const visibleItems = data.filter(item => item.visible);
        const sortedItems = visibleItems.sort((a, b) => a.group - b.group || a.order - b.order);  // Order by Group field then by Order field
        
        let prevGroupNum = 0;
        var menuItems = sortedItems.map(item => {
          let isBeginGroup = prevGroupNum !== item.group;
          let subMenu = [];
          prevGroupNum = item.group;
          let clickHandler = getActionContextMenu(item, cellValue, selectedRows, sortInfo );
          if (item.subItems != null)
          {
            subMenu = getActionContextSubMenu(item.subItems, cellValue, selectedRows, sortInfo );
          }
          return {
            text: item.associateInfo ? `${item.actionDisplayName} (${item.associateInfo})` : item.actionDisplayName,
            beginGroup: isBeginGroup,
            disabled: !item.enabled,
            items: item.subItems != null ? subMenu : null,
            onItemClick: clickHandler
          }
        });
        // show complete context menu with with data from backend server
        contextMenuRef.current.instance.option({ items: menuItems, showSubmenuMode:'onHover' });
      } catch (error) {
        appNotify(error.message, 'error');
        contextMenuRef.current.instance.hide();
      }
    }

    function getActionContextSubMenu(item, cellValue, selectedRows, sortInfo )
    {
      return item.map( opt => {
        let result = {
          text: opt.associateInfo ? `${opt.actionDisplayName} (${opt.associateInfo})` : opt.actionDisplayName,
          disabled: !opt.enabled
        };
        if (opt.subItems == null)
        {
          result.onItemClick = getActionContextMenu(opt, cellValue, selectedRows, sortInfo );
        }
        else 
        {
          result.items = getActionContextSubMenu(opt.subItems, cellValue, selectedRows, sortInfo );
        }
        return result;
      })
    }

    function getActionContextMenu(item, cellValue, selectedRows, sortInfo )
    {
      let clickHandler = () => console.log(`No click handler found for ${item.actionDisplayName}`);
      switch (item.action) {
        case 'CopyCell':
          clickHandler = () => copyCell(cellValue);
          break;
        case 'CopyRow':
          clickHandler = () => copyRow(selectedRows);
          break;
        // case 'CopyRowForImport':
        //   clickHandler = () => copyRowForImports(selectedRows, sortInfo);
        // break;
        case 'FilterBySelected':
          clickHandler = () => filterBySelected(selectedRows.map(opp => opp.id));
          break;
        case 'AssignTask':
          clickHandler = () => handleOpenAssign(selectedRows);
          break;
        case 'ClearAssignedUser':
          clickHandler = () => handleUnassign(selectedRows);
          break;
        case 'AssignTaskToMe':
          clickHandler = () => assignTasks('postAcqTask', selectedRows, user.id);
          break;
        case 'UnassignTaskFromMe':
          clickHandler = () => handleUnassign(selectedRows);
          break;
        case 'LoadTask':
          clickHandler = () => handleLoadTasks(selectedRows);
          break;
        case 'EditTaskDueDate':
          clickHandler = () => handleEditTask(selectedRows, columnSchema.find(col => col.field === 'dueDate'));
          break;
        case 'EditTaskImportName':
          clickHandler = () => handleEditTask(selectedRows, columnSchema.find(col => col.field === 'importName'));
          break;
        case 'ViewTask':
          clickHandler = () => push(`${Constants.Routes.POSTACQ_TASK}/${selectedRows[0].id}`);
          break;
        case 'DeleteTask':
          clickHandler = () => handleDeleteTasks(selectedRows);
          break;
        case 'CloseTask':
          clickHandler = () => handleCloseTasks(selectedRows);
          break;
        case 'OpenTask':
          clickHandler = () => handleOpenTasks(selectedRows);
          break;
        case 'ViewOnExplore':
          clickHandler = () => handleViewOnExplore(selectedRows);
          break;
        // case 'GetGUDPropertyDataRequest':
        //   clickHandler = () => getServiceRequest('GetGUDPropertyDataRequest',selectedRows);
        //   break;
        // case 'GetGUDPropertyDataWithNOIRequest':
        //   clickHandler = () => getServiceRequest('GetGUDPropertyDataWithNOIRequest',selectedRows);
        //   break;
        // case 'GetGUDMarketsRequest':
        //   clickHandler = () => getServiceRequest('GetGUDMarketsRequest',selectedRows);
        //   break;
        // case 'GetGUDTaxesRequest':
        //   clickHandler = () => getServiceRequest('GetGUDTaxesRequest',selectedRows);
        //   break;
        // case 'GetGUDRepairsEstimateRequest':
        //   clickHandler = () => getServiceRequest('GetGUDRepairsEstimateRequest',selectedRows);
        //   break;
        // case 'GetGUDCompsRequest':
        //   clickHandler = () => getServiceRequest('GetGUDCompsRequest',selectedRows);
        //   break;
        // case 'GetGUDExpectedSalesPriceRequest':
        //   clickHandler = () => getServiceRequest('GetGUDExpectedSalesPriceRequest',selectedRows);
        //   break;
        // case 'GetGUDTransactionEventHistoryRequest':
        //   clickHandler = () => getServiceRequest('GetGUDTransactionEventHistoryRequest',selectedRows);
        //   break;
        // case 'GetICBMOrdersRequest':
        //   clickHandler = () => getServiceRequest('GetICBMOrdersRequest',selectedRows);
        //   break;
        // case 'GetExploreImagesRequest':
        //   clickHandler = () => getServiceRequest('GetExploreImagesRequest',selectedRows);
        //   break;
        // case 'GetPropertyMasterPropertyRequest':
        //   clickHandler = () => getServiceRequest('GetPropertyMasterPropertyRequest',selectedRows);
        //   break;
        // case 'GetPropertyMasterJitPropertyRequest':
        //   clickHandler = () => getServiceRequest('GetPropertyMasterJitPropertyRequest',selectedRows);
        //   break;
        default:
          break;
      }
      return clickHandler;
    }
    function renderContextMenuItem(contextMenuItem, index) {
      /** When contextMenuItem.text is 'loading' we render a loading spinner instead of any text content,
       * which is a placeholer context menu we display before calling backend server to fetch real context menu items.
       * When contextMenuItem.text is not 'loading' it means our backend server call is finished and is populating
       * context menu with real data, now we just render the text content without any modification*/
      if (contextMenuItem.text === 'loading') {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}><CircularProgress size={16} /></div>;
      }
      else {
        return <React.Fragment>
          <span style={{ marginRight: '5px' }}>{contextMenuItem.text}</span>
          {contextMenuItem.items ? <span className="dx-icon-spinright" style={{ position: 'absolute', top: '7px', right: '2px' }}/> : null }
          </React.Fragment>;
      }
    }

    function handleContextMenuItemClick(e) {
      // This is triggered when user clicked an option in context menu
      // e.itemData.items is to check if this context menu option has subitems
      if (!e.itemData.items) {
        //console.log(`The "${e.itemData.text}" item was clicked`);
        e.itemData.onItemClick();
      }
    }

    function onHiddenContextMenu(e) { 
      // We Need to clear the current contextMenu after its closed, 
      // otherwise when user right clicks in the same grid cell, it does not re-fetch and thus we won't be able to get the most up-to-date context menu items 
      contextMenuRef.current.instance.option({ items: [], target: '#channelWorkflowGridContainer' }); 
    } 

    function findNestedValue(row, dataField) {
      if (!dataField) return undefined;
      let value = row;
      const split = dataField.split('.').filter(el => el !== '');

      for (const el of split) {
        if (value.hasOwnProperty(el)) {
          value = value[el];
        } else {
          value = undefined;
          break;
        }
      };
      return value;
    }

    function copyToClipboard(string) {
      const dummy = document.createElement('textarea');
      document.body.appendChild(dummy);
      dummy.value = string;
      dummy.select();
      document.execCommand('copy');
      document.body.removeChild(dummy);
    }

    const copyCell = cellValue => {
      copyToClipboard(cellValue);
      appNotify(' Cell copied to clipboard.', 'success', { autoHideDuration: 1500 });
    }

    const copyRow = (selectedRows) => {
      const visibleColumns = gridRef.current.instance.getVisibleColumns().filter(col => col.dataField);
      const headerString = visibleColumns.map(col => col.caption).join('\t') + '\r\n';
      const combinedString = selectedRows.reduce((string, row) => {
        const rowValues = [];
        visibleColumns.forEach(col => {
          const value = formatColumns(row,col,false);
          rowValues.push(value == null ? '\xa0' : value);
        });
        return string + rowValues.join('\t') + '\r\n';
      }, headerString);
      copyToClipboard(combinedString);
      appNotify(selectedRows.length + ' row(s) copied to clipboard.', 'success', { autoHideDuration: 1500 });
    }

    const copyRowForImports = async (selectedRows, sortInfo) => {
      const chlTemplateColumns = fetchColumnsForImport(selectedRows[0].channelText); // Must enforce same channel for selected rows
        const rowsinfo = await getRowInfo(selectedRows, chlTemplateColumns, sortInfo);
        const allHeaderString = chlTemplateColumns.map(col => col.displayName).join('\t') + '\r\n';
        const combinedString = rowsinfo.reduce((string, row) => {
          const rowValues = [];
          chlTemplateColumns.forEach(col => {
            const value = formatColumns(row,col,true);
            rowValues.push(fetchValidValueForColumn(rowsinfo.indexOf(row) + 1,value,col));
          });
          return string + rowValues.join('\t') + '\r\n';
        }, allHeaderString);
        copyToClipboard(combinedString);
        appNotify(selectedRows.length + ' row(s) copied to clipboard.', 'success', { autoHideDuration: 1500 });
    }

    function fetchValidValueForColumn(index, value, col)
    {
      switch (col.field) {
        case 'opportunitySource':
          return '';
        case 'sourceId':
          return index;
        case 'notes':
          return value.replace(/(?:\r\n|\r|\n|\t)/g, '');
        default:
          return value;
      }
    }

    const formatColumns = (row, col, isForImport) => {
      const type = isForImport ? col.type : col.dataType;
      const field = isForImport ? col.field : col.dataField;
      let value = findNestedValue(row, field);;
      switch (type) {
        case 'date':
          return value == null ? '\xa0' : dateFormat(new Date(value), 'MM/dd/yyyy');
        case 'datetime':
          return value == null ? '\xa0' : new Date(value).toLocaleString(undefined, {year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hour12: true, minute:'2-digit'});
        default:
          return value == null ? '\xa0' : value;
      }
    }

    function fetchColumnsForImport(channel)
    {
      return appSettings.FieldSettings.filter(field => field.importGrid?.visible && field.channels.includes(channel));
    }

    async function getRowInfo(selectedRows, chlTemplateColumns, sortInfo)
    {
      const url = '/api/underwrite/workflow/opportunitygrid';
      const ids = [];
      const columns = chlTemplateColumns.map(col => col.field);
      selectedRows.forEach(col => {
        if (selectedRows[selectedRows.length -1] === col)
          {
            ids.push(["id","=", col.id])
          }else{
            ids.push(["id","=", col.id])
            ids.push("or");
          }
      });
      const result = await  postWithToken(url, 
        {
          loadOptions: {
            filter: ids,
            requireTotalCount : false,
            select: columns,
            skip:0,
            sort: sortInfo ? sortInfo : [{selector:"id", desc: true}]
          }
        });
      const jsonResult = await result.json();
      return jsonResult.data;
    }

    const getServiceRequest = async (option, selectedRows) =>{
      const serviceRequest = await fetchServiceRequestData(option, selectedRows);
      let combinedString = serviceRequest.url != null ? serviceRequest.url + '\r\n\r\n': "";
      combinedString += serviceRequest.headers != null ? getHeadersString(serviceRequest.headers):"";
      combinedString += serviceRequest.body != null ? serviceRequest.body.replace('\t',''):"";
      copyToClipboard(combinedString);
      appNotify(serviceRequest.requestName + ' request copied to clipboard.', 'success', { autoHideDuration: 1500 });
    }
    function getHeadersString(headers)
    {
      let result = "";
      headers.forEach(header => {
        result += header + '\r\n\r\n';
      });
      return result;
    }

    async function fetchServiceRequestData(option, selectedRows)
    {
      const url = `/api/admin/Utilities/${option}`;
      const txnOppIds = selectedRows.map(col => col.id);
      const result = await postWithToken(url, txnOppIds);
      const jsonResult = await result.json();
      return jsonResult.data;
    }

    /**Filter by selected */
    function filterBySelected(ids) {
      const newState = { ...getGridRefState() };
      newState.filterPanel.filterEnabled = true;
      newState.filterValue = ['id', 'anyof', ids];
      gridRef.current.instance.state(newState);
    }
    //#endregion

    // <SvgIcon color="primary" fontSize="large"><path fill="#000000" d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2Z" /></SvgIcon>
    
    let columns = useMemo(() => {

      return getColumns().map(({
        field,
        displayName,
        width,
        type,
        format,
        fixed,
        visible,
        showInColumnChooser
      }, index) => {
        // let { fixed, visible, showInColumnChooser } = channelWorkflowGrid;
        // const index = appSettings.FieldIndex.channelWorkflowGrid[field];


        let allowSearch = true;
        if ([
          // 'hadSuccessfulPrismPush',
          // 'descriptiveFit',
          // 'descriptiveRentalFit',
          // 'descriptiveRetailFit',
          // 'lastPrismPushDidSucceed',
          // 'isCompleted',
          // 'hasPool'
        ].includes(field) || type === 'date' || type === 'datetime') {
          allowSearch = false;
        }

        return (
          <Column
            key={field}
            dataField={field}
            caption={displayName}
            fixed={fixed || false}
            width={width}
            dataType={type}
            format={format}
            allowSorting={true}
            allowGrouping={true}
            allowExporting={true}
            allowHeaderFiltering={type !== 'datetime'}
            visible={visible}
            showInColumnChooser={showInColumnChooser !== false}
            visibleIndex={index}
            // showWhenGrouped={true}
            showEditorAlways={false}
            trueText={'✔'}
            falseText={'✖'}
            calculateFilterExpression={calculateFilterExpression}
          >
            <HeaderFilter
              groupInterval={null}
              search={{
                enabled: allowSearch,
                mode: type === 'number' ? 'startswith' : 'contains'
              }}
            />
          </Column>
        )
      });
    }, [dataSource, getColumns]);

    const getActiveReasonFriendlyDisplay = (activeReason) => {
      switch (activeReason) {
        case Constants.ActiveReason.NEWLISTING:
           return 'New Listing'
        case Constants.ActiveReason.PRICECHANGE:
           return 'Price Change'
        case Constants.ActiveReason.BACKONMARKET:
           return 'Back on Market'
        case Constants.ActiveReason.STALE:
           return 'Stale'
        default:
           return '';
      }
    }

    const onCellPrepared = cell => {
      if (cell.column.dataField === 'taskStatus' && cell.data?.taskStatus) {
        const status = getTaskChipStatus(cell.data);
        if (!status) return;
        cell.cellElement.style.backgroundColor = status.color;
        cell.cellElement.style.color = '#fff';
        cell.cellElement.style.textAlign = 'center';
        cell.cellElement.textContent = status.text;
      }
      if (cell.column.dataField === 'underwriteStatus' && cell.data?.underwriteStatus) {
        const status = getUnderwriteChipStatus(cell.data);
        if (!status) return;
        cell.cellElement.style.backgroundColor = status.backgroundColor;
        cell.cellElement.style.color = status.color ?? '#fff';
        cell.cellElement.style.textAlign = 'center';
        cell.cellElement.textContent = status.text;
      }
      if (cell.rowType === 'data' && cell.column.dataType === 'boolean') {
        cell.cellElement.style.fontSize = '20px';
        cell.cellElement.style.color = 'rgba(0, 0, 0, 0.65)';
      }
    }

    const onCellDblClick = (e) => {
      if (e?.data?.id) {
        if (e.data.postAcquisitionLoadCompletedOn) {
          push(`${Constants.Routes.POSTACQ_TASK}/${e.data.id}`);
        } else {
          appNotify('Task data must be loaded to open underwrite page.', 'warning');
        }
      }
    }

    const sortFields = useMemo(() => { 
      let fields = getColumns().map(c => ({ 
        dataField: c.field,
        caption: c.displayName,
        dataType: c.type,
        format: c.format,
        filterOperations: c.type === 'datetime' ? [
          "=",
          "<>",
          "<",
          ">",
          "<=",
          ">=",
          "isblank",
          "isnotblank",
          "between",
          "since5pmYesterday",
          "isYesterday",
          "isToday",
          "isTomorrow",
          "isThisWeek",
          "isThisMonth"
        ] : undefined,
        lookup: c.channelWorkflowGrid?.values ? {
          dataSource: c.channelWorkflowGrid.values
        } : undefined
      }));
      let getCaption = f => f.caption.length > 0 ? f.caption.toLowerCase() : f.dataField.toLowerCase();
      fields.sort((a, b) => {
        if (getCaption(a) < getCaption(b))
          return -1;
        if (getCaption(a) > getCaption(b))
          return 1;
        return 0;
      });
      // console.log(fields)
      return fields;
    }, [getColumns]);

    function onOptionChanged(option) {
      const columnChanges = ['visible', 'visibleIndex', 'width', 'groupIndex', 'sortIndex', 'sortOrder'];
      if (listenForDivertedChanges.current === true && (option.name === 'filterValue' ||
        // option.fullName.includes('sortOrder') ||
        (option.name === 'columns' && columnChanges.some(el => option.fullName.includes(el)))
      ) && !postAcqWorkflowGridLayoutIsDiverted) {
        setPostAcqWorkflowGridLayoutIsDiverted(true);
      }
    }

    function handleKeyDown(e) {
      if (!e?.event) return;
      const { originalEvent } = e.event;
      if (originalEvent && originalEvent.keyCode === 65 && originalEvent.ctrlKey) { // Capture Ctrl + A
        if (!visibleRowsCount) {
          return;
        } else {
          originalEvent.preventDefault();
        }
        if (selectAllIsLoading.current) return;
        if (visibleRowsCount > contextMenuMaxRowCount) {
          appNotify(`Select All is not supported if more than ${contextMenuMaxRowCount.toLocaleString()} rows are visible`, 'warning');
        } else {
          selectAllIsLoading.current = true;
          gridRef.current.instance.selectAll().then(() => {
            selectAllIsLoading.current = false;
          });
        }
      }
    }

    return (
      <div className={classes.gridContainer} id="channelWorkflowGridContainer">
        <DataGrid
          id='postAcqWorkflowGrid' // Must match in ReVUeSetting.json for tooltips to work
          ref={gridRef}
          allowColumnReordering={true}
          height={'calc(100vh - 220px)'}
          hoverStateEnabled={true}
          onToolbarPreparing={onToolbarPreparing}
          onContextMenuPreparing={handleContextMenu}
          highlightChanges={true}
          onContentReady={onContentReady}
          onOptionChanged={onOptionChanged}
          onCellDblClick={onCellDblClick}
          remoteOperations={true}
          onCellPrepared={onCellPrepared}
          dateSerializationFormat={dateSerializationFormat}
          enableTooltips={true}
          onKeyDown={handleKeyDown}
          tooltips={columnSchema}
        >
          <ColumnFixing enable={true} />
          <FilterPanel visible={true} />
          <FilterBuilder fields={sortFields}>
            {dataGridExtension.filterBuilderCustomOperations}
          </FilterBuilder>
          <FilterBuilderPopup />
          <HeaderFilter visible={true} search={{ enabled: true }} hideSelectAllOnSearch={false} />
          <Sorting mode={'multiple'} />
          <GroupPanel visible={true} />
          <SearchPanel visible={true} searchVisibleColumnsOnly={true} />
          <Grouping />
          <Scrolling
            columnRenderingMode='virtual'
          />
          <Selection mode={'multiple'} showCheckBoxesMode={false} allowSelectAll={false}/>
          <Paging defaultPageSize={50} />
          <Pager format={',##0.###'} visible={true} showInfo={true} showPageSizeSelector={true} allowedPageSizes={[25, 50, 100]} />
          <StateStoring
            enabled={false}
            type={'custom'}
            storageKey={'storage'}
            customSave={state => console.log(state)}
            ignoreColumnOptionNames={[]}
          />
          <Template name='totalCount' render={toolbarItemRender} />
          <Template name='excludedCount' render={toolbarItemRender} />
          {columns}
          <Column width='auto' showInColumnChooser={false} visibleIndex={9999} allowReordering={false} />
        </DataGrid>
        <CustomColumnChooser
          container="#workflowGrid"
          visible={isColumnChooserVisible}
          onHiding={() => setIsColumnChooserVisible(false)}
          columns={columns}
          gridRef={gridRef}
        />
        <ContextMenu
          ref={contextMenuRef}
          onItemClick={handleContextMenuItemClick}
          itemRender={renderContextMenuItem}
          onHidden={onHiddenContextMenu}
          target='#channelWorkflowGridContainer'
        />
      </div>
    );
  });

const styles = theme => ({
  gridContainer: {
    padding: '5px 8px 8px'
  },
  informer: {
    display: 'flex',
    flexFlow: 'column',
    justifyContent: 'center',
    width: '130px',
    background: '#80a4c7',
    color: '#fff',
    textAlign: 'center'
  },
  exclusionInformer: {
    display: 'flex',
    flexFlow: 'column',
    justifyContent: 'center',
    width: '130px',
    background: '#67c12e',
    color: '#fff',
    textAlign: 'center'
  },
  countLabel: {
    fontSize: '18px',
    paddingTop: '5px',
    margin: '0'
  },
  totalCountText: {
    paddingBottom: '5px',
  },
});

const mapState = state => {
  return {
    user: state.appState.user,
    postAcqWorkflowGridLayouts: state.postAcqWorkflowState.postAcqWorkflowGridLayouts,
    postAcqWorkflowGridState: state.postAcqWorkflowState.postAcqWorkflowGridState,
    postAcqWorkflowGridDataUpdate: state.postAcqWorkflowState.postAcqWorkflowGridDataUpdate,
    postAcqWorkflowGridOldestLayoutId: postAcqWorkflowGridOldestLayoutIdSelector(state),
    postAcqWorkflowGridLayoutIsDiverted: state.postAcqWorkflowState.postAcqWorkflowGridLayoutIsDiverted,
    appSettings: state.appState.appSettings,
  }
};

const mapDispatch = {
  push,
  appNotify,
  setPostAcqWorkflowGridLayoutIsDiverted,
  setPostAcqWorkflowGridNeedsToRefresh,
  setPostAcqWorkflowGridSelectedLayoutId
};

export default compose(
  withStyles(styles),
  connect(
    mapState,
    mapDispatch,
    null,
    { forwardRef: true }
  )
)(ChannelWorkflowGrid);
